'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, boolean as __eliadmin_zod_2, array as __eliadmin_zod_3, number as __eliadmin_zod_4 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Paragraph } from '../ui/paragraph';
import {
  fetchDynamicSectionsById,
  type TDynamicData,
} from '@eliadmin/core/src/api/dynamics/dynamics-api';
import Link from 'next/link';
import { Field } from '@eliadmin/core/src/components/form/field';
import { MdOutlineEmail } from 'react-icons/md';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { VscSend } from 'react-icons/vsc';

const contactSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: __eliadmin_zod_1().default(''),
    custom: __eliadmin_zod_0({
      interest: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: __eliadmin_zod_1(),
    gdpr: __eliadmin_zod_2().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    attachments: __eliadmin_zod_3(__eliadmin_zod_0({ id: __eliadmin_zod_4() })).optional(),
    'g-recaptcha-response': __eliadmin_zod_1().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      phone: '',
      custom: {
        interest: '',
      },
      email: '',
      text: '',
      gdpr: false,
      attachments: [],
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const { data: interests = [] } = useQuery({
    queryKey: ['INTERESTS', locale],
    queryFn: () => fetchDynamicSectionsById<TDynamicData>({ locale, id: 3 }),
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 rounded-[20px] p-4 sm:p-6 md:p-8 lg:gap-8 lg:p-12',
          'ea-contact-form',
        )}
      >
        <div className="grid w-full grid-cols-2 gap-3 sm:gap-4">
          <Field
            required
            control={form?.control as any}
            name="name"
            placeholder={t('text3179')}
            label={t('text3179')}
            className={{ wrapper: 'col-span-2 w-full' }}
          />
          <Field
            required
            control={form?.control as any}
            name="phone"
            type="tel"
            placeholder={t('text1258')}
            label={t('text1258')}
            className={{ wrapper: 'col-span-2 w-full sm:col-span-1' }}
          />
          <Field
            required
            control={form?.control as any}
            name="email"
            type="email"
            placeholder="E-mail"
            label="E-mail"
            className={{ wrapper: 'col-span-2 w-full sm:col-span-1' }}
            before={<MdOutlineEmail size={20} className="text-[#9CA3AF]" />}
          />

          <Field
            required
            control={form?.control as any}
            name="custom.interest"
            type="select"
            options={interests.map(interest => ({
              value: interest.name,
              label: interest.name,
            }))}
            placeholder={t('text3714')}
            label={t('text3714')}
            className={{ wrapper: 'col-span-2 w-full' }}
          />
          <Field
            control={form?.control as any}
            name="text"
            type="textarea"
            placeholder={t('text2340')}
            label={t('text2340')}
            className={{ wrapper: 'col-span-2 w-full' }}
          />
        </div>
        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'col-span-12 flex items-start',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                />
              </FormControl>
              <FormLabel className={cn('ea-contact-form__gdpr-checkbox-label')}>
                <Paragraph size="base" className="w-full font-normal">
                  {t('text613')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.(), locale, 'gdpr')}
                    target="_blank"
                    className="generic-hover font-bold text-primary"
                  >
                    {t('text614')}
                  </Link>{' '}
                  {t('text3354')}
                </Paragraph>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12', 'hidden')}>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full items-start justify-start">
          <Button
            type="submit"
            className={cn('w-full sm:w-fit', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
          >
            <VscSend size={20} /> {t('text185')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
